import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';

import { addFileMetadata } from '@/store/slices/poc/heliaSlice';
import { enqueueFile } from '@/store/slices/poc/cloneQueueSlice';
import { fetchSixDetailMetaAndFilesThunk } from '@/store/thunks/sixDetail/fetchMetaAndFilesThunk';
import { processCloneQueue } from '@/store/thunks/poc/cloneQueue/processCloneQueue';
import { parseCid } from '@/services/poc/cidUtils';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';
import {
  isIncomingMessageOfType,
  type IncomingMessageOfType,
} from '@/store/listeners/poc/incomingMessageMatchers';
import {
  cancelAllFileRefreshPolls,
  ensureFileRefreshPoll,
} from '@/store/listeners/poc/fileRefreshCoordinator';
import type { AppStartListening } from '@/store/listeners/types';

export function registerIncomingFileRefreshListeners(
  startAppListening: AppStartListening
): void {
  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'file-ready-for-download'>> =>
      isIncomingMessageOfType(action, 'file-ready-for-download'),
    effect: async(action, listenerApi) => {
      const { sixId } = action.payload;
      const { cid, metadata, sender } = action.payload.result.data;
      const log = listenerApi.extra.loggerService.createLogger({ namespace: 'six3ro:p2p:messaging' });

      ensureFileRefreshPoll(sixId, listenerApi);

      const heliaNode = listenerApi.extra.pocServices.p2pStackManagerService.getHeliaNode(sixId);
      if (heliaNode === null) {
        return;
      }

      try {
        const fileExists = await listenerApi.extra.pocServices.fileStoreService.fileExists(
          heliaNode,
          parseCid(cid)
        );

        if (fileExists) {
          listenerApi.dispatch(addFileMetadata(metadata));
          return;
        }

        listenerApi.dispatch(enqueueFile({
          sixId,
          cid,
          metadata,
          size: metadata.size,
          priority: 'normal',
        }));
        void listenerApi.dispatch(processCloneQueue());
      } catch (error) {
        log.warn(`[${sixId}] Error checking file existence for ${cid} from ${sender}`, error);
      }
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'files-updated'>> =>
      isIncomingMessageOfType(action, 'files-updated'),
    effect: (action, listenerApi) => {
      ensureFileRefreshPoll(action.payload.sixId, listenerApi);
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'folder-created'>> =>
      isIncomingMessageOfType(action, 'folder-created'),
    effect: async(action, listenerApi) => {
      await listenerApi.dispatch(fetchSixDetailMetaAndFilesThunk(action.payload.sixId)).unwrap();
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'folder-deleted'>> =>
      isIncomingMessageOfType(action, 'folder-deleted'),
    effect: async(action, listenerApi) => {
      await listenerApi.dispatch(fetchSixDetailMetaAndFilesThunk(action.payload.sixId)).unwrap();
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'file-deleted'>> =>
      isIncomingMessageOfType(action, 'file-deleted'),
    effect: async(action, listenerApi) => {
      await listenerApi.dispatch(fetchSixDetailMetaAndFilesThunk(action.payload.sixId)).unwrap();
    },
  });

  startAppListening({
    actionCreator: logoutThunk.pending,
    effect: () => {
      cancelAllFileRefreshPolls();
    },
  });
}
